/**
 * Jagad Jawa — Portal Budaya Luhur Nusantara
 * Modular Entry Point & Orchestrator (ES Modules)
 * 
 * Tanggung jawab main.js:
 * 1. Menghubungkan modul-modul fitur domain ke namespace global/window (thin wrappers).
 * 2. Mengorkestrasi pergantian tab (tab-switched) dan lazy loading dataset berat.
 * 3. Menjalankan bootstrap awal (Kalender, badge hari ini, modal listeners).
 */

// ─── UI CORE & MODAL CONTROLLER ───────────────────────────────────────────
import './ui/navigation.js';
import { showToast, copyToClipboard } from './ui/toast.js';
import { closeAnyActiveModal, initModalListeners } from './ui/modal.js';
import { initI18n, toggleLanguage, setLanguage, getLanguage } from './ui/i18n.js';
import {
  getMode,
  setMode,
  isPemula,
  isAhli,
  hasModePreference,
  applyModeToUI,
  toggleMode,
  showOnboardingModal,
  closeOnboardingModal,
  pilihModeAwal,
  initModeFeature
} from './ui/mode.js';
import { initThemeFeature } from './ui/theme.js';
import { initKelirWayangFeature } from './ui/kelir-wayang.js';
import { initStickyToc } from './ui/navigation.js';
import {
  renderHeroTodayCard,
  initAllFormRealtimeCalculators,
  toggleTermTooltip,
  initJodohWizard,
  setJodohWizardStep,
  drawShareCardQrCode,
  syncDocumentLangAttribute,
  saveUserProfileWeton,
  loadUserProfileWeton
} from './ui/ux-enhancements.js';

// ─── DOMAIN FEATURES & WIRING ─────────────────────────────────────────────
import { wireKalenderFeature, initQuickTodayBadge } from './features/kalender.js';
import { printLaporanKalender, downloadKalenderPdf, downloadKalenderPng, resetKalenderToday } from './modules/kalender/kalender-ui.js';
import { wireJodohFeature } from './features/jodoh.js';
import { wireSelametanFeature } from './features/selametan.js';
import { wireAksaraFeature } from './features/aksara.js';
import { wireWayangFeature } from './features/wayang.js';
import { wireAudioFeature } from './features/audio.js';
import {
  wireNujumFeature,
  loadScript,
  ensureNujumLoaded,
  ensurePituturLoaded,
  ensureWukuLoaded,
  ensureTumpengLoaded
} from './features/nujum.js';

// ─── SAPA DINA (PHASE II) ───────────────────────────────────────────────────
import { wireSapaDinaFeature, initSapaDina } from './features/sapa-dina.js';
import { invalidateDomainData } from './services/dbLoader.js';

// ─── LAZY LOADERS & IN-MEMORY CACHE (HIGH PERFORMANCE & ANTI-OVERHEATING) ───
const lazyModuleCache = new Map();

async function getOrImportModule(key, importPromiseFn) {
  if (lazyModuleCache.has(key)) return lazyModuleCache.get(key);
  const mod = await importPromiseFn();
  lazyModuleCache.set(key, mod);
  if (typeof window !== 'undefined') {
    Object.assign(window, mod);
  }
  return mod;
}

export const ensureIjabLoaded = () => getOrImportModule('ijab', () => import('./modules/ijab/ijab-ui.js'));
export const ensureOmahLoaded = () => getOrImportModule('omah', () => import('./modules/omah/omah-ui.js'));
export const ensureTernakLoaded = () => getOrImportModule('ternak', () => import('./modules/petung-kehidupan/petung-kehidupan-ui.js'));
export const ensureSasmithaLoaded = () => getOrImportModule('sasmitha', () => import('./modules/sasmitha/sasmitha-ui.js'));
export const ensurePustakaLoaded = () => getOrImportModule('pustaka', () => import('./modules/pustaka/pustaka-ui.js'));
export const ensureSinengkerLoaded = () => getOrImportModule('sinengker', () => import('./modules/sinengker/sinengker-ui.js'));
export const ensureMitologiLoaded = () => getOrImportModule('mitologi', () => import('./modules/budaya/mitologi-ui.js'));
export const ensurePadewanLoaded = () => getOrImportModule('padewan', () => import('./modules/budaya/padewan-ui.js'));
export const ensureNujumCardLoaded = () => getOrImportModule('nujumCard', () => import('./modules/nujum/nujum-share-card.js'));
export const ensureSapaDinaShareLoaded = () => getOrImportModule('sapaDinaShare', () => import('./modules/sapa-dina/sapa-dina-ui.js'));

function makeLazyProxy(loaderFn, fnName) {
  return async function(...args) {
    const mod = await loaderFn();
    if (mod && typeof mod[fnName] === 'function') {
      return mod[fnName](...args);
    }
  };
}

// ─── INITIAL WIRING TO WINDOW (THIN WRAPPERS UNTUK INLINE HTML ONCLICK) ────
if (typeof window !== 'undefined') {
  // Padewan
  window.openPadewanModal = makeLazyProxy(ensurePadewanLoaded, 'openPadewanModal');
  window.closePadewanModal = makeLazyProxy(ensurePadewanLoaded, 'closePadewanModal');
  window.selectPadewanCard = makeLazyProxy(ensurePadewanLoaded, 'selectPadewanCard');
  window.renderPadewanGrid = makeLazyProxy(ensurePadewanLoaded, 'renderPadewanGrid');
  window.renderPadewanDetail = makeLazyProxy(ensurePadewanLoaded, 'renderPadewanDetail');

  // Nujum Pokémon & Visual Share Cards
  window.openSapaDinaShareModal = makeLazyProxy(ensureSapaDinaShareLoaded, 'openSapaDinaShareModal');
  window.closeSapaDinaShareModal = makeLazyProxy(ensureSapaDinaShareLoaded, 'closeSapaDinaShareModal');
  window.downloadSapaDinaCardPng = makeLazyProxy(ensureSapaDinaShareLoaded, 'downloadSapaDinaCardPng');
  window.shareSapaDinaVisual = makeLazyProxy(ensureSapaDinaShareLoaded, 'shareSapaDinaVisual');
  window.openNujumPokemonCardModal = makeLazyProxy(ensureNujumCardLoaded, 'openNujumPokemonCardModal');
  window.closeNujumPokemonCardModal = makeLazyProxy(ensureNujumCardLoaded, 'closeNujumPokemonCardModal');
  window.downloadNujumPokemonCardPng = makeLazyProxy(ensureNujumCardLoaded, 'downloadNujumPokemonCardPng');
  window.shareNujumPokemonCard = makeLazyProxy(ensureNujumCardLoaded, 'shareNujumPokemonCard');
  window.onNujumPokemonOptionChange = makeLazyProxy(ensureNujumCardLoaded, 'onNujumPokemonOptionChange');
  window.onNujumPokemonNameInput = makeLazyProxy(ensureNujumCardLoaded, 'onNujumPokemonNameInput');
  window.generateDraftKartuKarakter = makeLazyProxy(ensureNujumCardLoaded, 'generateDraftKartuKarakter');
  window.drawKartuKarakter = makeLazyProxy(ensureNujumCardLoaded, 'drawKartuKarakter');
  window.openKartuKarakterModal = makeLazyProxy(ensureNujumCardLoaded, 'openKartuKarakterModal');
  window.closeKartuKarakterModal = makeLazyProxy(ensureNujumCardLoaded, 'closeKartuKarakterModal');
  window.downloadKartuKarakterPng = makeLazyProxy(ensureNujumCardLoaded, 'downloadKartuKarakterPng');
  window.shareKartuKarakter = makeLazyProxy(ensureNujumCardLoaded, 'shareKartuKarakter');

  window.showToast = showToast;
  window.copyToClipboard = copyToClipboard;
  window.toggleLanguage = toggleLanguage;
  window.setLanguage = setLanguage;
  window.getLanguage = getLanguage;
  window.getMode = getMode;
  window.setMode = setMode;
  window.isPemula = isPemula;
  window.isAhli = isAhli;
  window.hasModePreference = hasModePreference;
  window.applyModeToUI = applyModeToUI;
  window.toggleMode = toggleMode;
  window.showOnboardingModal = showOnboardingModal;
  window.closeOnboardingModal = closeOnboardingModal;
  window.pilihModeAwal = pilihModeAwal;

  // Kalender Jawa Export Bindings
  window.printLaporanKalender = printLaporanKalender;
  window.downloadKalenderPdf = downloadKalenderPdf;
  window.downloadKalenderPng = downloadKalenderPng;
  window.resetKalenderToday = resetKalenderToday;

  // Pustaka Dongo, Usada & Kautaman bindings
  window.initPustakaUI = makeLazyProxy(ensurePustakaLoaded, 'initPustakaUI');
  window.renderPustakaCards = makeLazyProxy(ensurePustakaLoaded, 'renderPustakaCards');
  window.switchPustakaKategori = makeLazyProxy(ensurePustakaLoaded, 'switchPustakaKategori');
  window.onPustakaSearchInput = makeLazyProxy(ensurePustakaLoaded, 'onPustakaSearchInput');
  window.clearPustakaSearch = makeLazyProxy(ensurePustakaLoaded, 'clearPustakaSearch');
  window.openPustakaReader = makeLazyProxy(ensurePustakaLoaded, 'openPustakaReader');
  window.closePustakaReader = makeLazyProxy(ensurePustakaLoaded, 'closePustakaReader');
  window.onPustakaNameChange = makeLazyProxy(ensurePustakaLoaded, 'onPustakaNameChange');
  window.copyPustakaCurrentDoa = makeLazyProxy(ensurePustakaLoaded, 'copyPustakaCurrentDoa');
  window.switchPustakaSubtab = makeLazyProxy(ensurePustakaLoaded, 'switchPustakaSubtab');
  window.onKautamanSearch = makeLazyProxy(ensurePustakaLoaded, 'onKautamanSearch');
  window.copyKautamanItem = makeLazyProxy(ensurePustakaLoaded, 'copyKautamanItem');
  window.onUsadaSearch = makeLazyProxy(ensurePustakaLoaded, 'onUsadaSearch');
  window.filterUsadaPenyakit = makeLazyProxy(ensurePustakaLoaded, 'filterUsadaPenyakit');
  window.copyUsadaItem = makeLazyProxy(ensurePustakaLoaded, 'copyUsadaItem');
  window.copyKalacakraFull = makeLazyProxy(ensurePustakaLoaded, 'copyKalacakraFull');
  window.openPustakaNaskahReader = makeLazyProxy(ensurePustakaLoaded, 'openPustakaNaskahReader');
  window.setNaskahReaderPupuh = makeLazyProxy(ensurePustakaLoaded, 'setNaskahReaderPupuh');
  window.unduhNaskahKuno = makeLazyProxy(ensurePustakaLoaded, 'unduhNaskahKuno');
  window.copyNaskahFull = makeLazyProxy(ensurePustakaLoaded, 'copyNaskahFull');
  window.onNaskahSearch = makeLazyProxy(ensurePustakaLoaded, 'onNaskahSearch');
  window.openPustakaReferensiReader = makeLazyProxy(ensurePustakaLoaded, 'openPustakaReferensiReader');
  window.unduhDokumenReferensi = makeLazyProxy(ensurePustakaLoaded, 'unduhDokumenReferensi');
  window.copyReferensiDoc = makeLazyProxy(ensurePustakaLoaded, 'copyReferensiDoc');
  window.onReferensiSearch = makeLazyProxy(ensurePustakaLoaded, 'onReferensiSearch');
  window.onKamusSearch = makeLazyProxy(ensurePustakaLoaded, 'onKamusSearch');
  window.switchKamusType = makeLazyProxy(ensurePustakaLoaded, 'switchKamusType');
  window.unduhKamus = makeLazyProxy(ensurePustakaLoaded, 'unduhKamus');
  window.copyKamusWord = makeLazyProxy(ensurePustakaLoaded, 'copyKamusWord');

  // Pustaka Sinengker bindings
  window.initSinengkerUI = makeLazyProxy(ensureSinengkerLoaded, 'initSinengkerUI');
  window.submitSinengkerPin = makeLazyProxy(ensureSinengkerLoaded, 'submitSinengkerPin');
  window.lockSinengkerUI = makeLazyProxy(ensureSinengkerLoaded, 'lockSinengkerUI');
  window.switchSinengkerSubtab = makeLazyProxy(ensureSinengkerLoaded, 'switchSinengkerSubtab');
  window.onKompasDanyangSearch = makeLazyProxy(ensureSinengkerLoaded, 'onKompasDanyangSearch');
  window.setKompasDanyangSample = makeLazyProxy(ensureSinengkerLoaded, 'setKompasDanyangSample');
  window.copyKompasDanyangResult = makeLazyProxy(ensureSinengkerLoaded, 'copyKompasDanyangResult');
  window.copyMendhemAriAri = makeLazyProxy(ensureSinengkerLoaded, 'copyMendhemAriAri');
  window.copyUbarampePager = makeLazyProxy(ensureSinengkerLoaded, 'copyUbarampePager');
  window.copySastraJendra = makeLazyProxy(ensureSinengkerLoaded, 'copySastraJendra');
  window.copyBodroSampir = makeLazyProxy(ensureSinengkerLoaded, 'copyBodroSampir');
  window.copyKasedanJati = makeLazyProxy(ensureSinengkerLoaded, 'copyKasedanJati');
  window.copyRosoSejati = makeLazyProxy(ensureSinengkerLoaded, 'copyRosoSejati');
  window.copyAjiMantra = makeLazyProxy(ensureSinengkerLoaded, 'copyAjiMantra');
  window.copyRuwatanMantra = makeLazyProxy(ensureSinengkerLoaded, 'copyRuwatanMantra');
  window.openKompasImageZoomModal = makeLazyProxy(ensureSinengkerLoaded, 'openKompasImageZoomModal');
  window.closeKompasImageZoomModal = makeLazyProxy(ensureSinengkerLoaded, 'closeKompasImageZoomModal');
  window.switchKompasViewMode = makeLazyProxy(ensureSinengkerLoaded, 'switchKompasViewMode');

  // Mitologi Nusantara bindings
  window.initMitologiUI = makeLazyProxy(ensureMitologiLoaded, 'initMitologiUI');
  window.openMitologiModal = makeLazyProxy(ensureMitologiLoaded, 'openMitologiModal');
  window.closeMitologiModal = makeLazyProxy(ensureMitologiLoaded, 'closeMitologiModal');
  window.copyMitologiStory = makeLazyProxy(ensureMitologiLoaded, 'copyMitologiStory');
  window.toggleMitologiFontSize = makeLazyProxy(ensureMitologiLoaded, 'toggleMitologiFontSize');

  // Wuku Category Filter dispatcher
  window.setWukuCategoryFilter = async function(category) {
    await ensureWukuLoaded();
    const wukuUI = await import('./modules/wuku/wuku-ui.js');
    if (wukuUI && typeof wukuUI.setWukuCategoryFilter === 'function') {
      wukuUI.setWukuCategoryFilter(category);
    }
  };

  // Ijab & Omah bindings
  window.initIjabUI = makeLazyProxy(ensureIjabLoaded, 'initIjabUI');
  window.syncIjabDariTanggal = makeLazyProxy(ensureIjabLoaded, 'syncIjabDariTanggal');
  window.hitungIjabDariUI = makeLazyProxy(ensureIjabLoaded, 'hitungIjabDariUI');
  window.setIjabWizardStep = makeLazyProxy(ensureIjabLoaded, 'setIjabWizardStep');
  window.toggleIjabFormMode = makeLazyProxy(ensureIjabLoaded, 'toggleIjabFormMode');
  window.terapkanPrefillJodohKeIjab = makeLazyProxy(ensureIjabLoaded, 'terapkanPrefillJodohKeIjab');
  window.tutupBannerPrefillJodoh = makeLazyProxy(ensureIjabLoaded, 'tutupBannerPrefillJodoh');
  window.periksaPrefillJodoh = makeLazyProxy(ensureIjabLoaded, 'periksaPrefillJodoh');
  window.initOmahUI = makeLazyProxy(ensureOmahLoaded, 'initOmahUI');
  window.setOmahWizardStep = makeLazyProxy(ensureOmahLoaded, 'setOmahWizardStep');
  window.populateOmahSelects = makeLazyProxy(ensureOmahLoaded, 'populateSelects');
  window.syncOmahDariTanggal = makeLazyProxy(ensureOmahLoaded, 'syncOmahDariTanggal');
  window.hitungOmahDariUI = makeLazyProxy(ensureOmahLoaded, 'hitungOmahDariUI');
  window.cariHariBaikOmahUI = makeLazyProxy(ensureOmahLoaded, 'cariHariBaikOmahUI');
  window.pilihTanggalHasilCariOmah = makeLazyProxy(ensureOmahLoaded, 'pilihTanggalHasilCariOmah');
  window.toggleOmahFormMode = makeLazyProxy(ensureOmahLoaded, 'toggleOmahFormMode');
  window.setOmahDataset = async (...args) => {
    const mod = await import('./modules/omah/omah-engine.js');
    return mod.setOmahDataset(...args);
  };
  window.invalidateDomainData = invalidateDomainData;

  // Ternak, Loro, & Geblak bindings
  window.initTernakUI = makeLazyProxy(ensureTernakLoaded, 'initTernakUI');
  window.syncTernakDariTanggal = makeLazyProxy(ensureTernakLoaded, 'syncTernakDariTanggal');
  window.hitungTernakDariUI = makeLazyProxy(ensureTernakLoaded, 'hitungTernakDariUI');
  window.resetTernakUI = makeLazyProxy(ensureTernakLoaded, 'resetTernakUI');
  window.setTernakTujuan = makeLazyProxy(ensureTernakLoaded, 'setTernakTujuan');
  window.hitungTernakDariSelect = makeLazyProxy(ensureTernakLoaded, 'hitungTernakDariSelect');
  window.switchTernakSubtab = makeLazyProxy(ensureTernakLoaded, 'switchTernakSubtab');
  window.konfirmasiBukaGeblak = makeLazyProxy(ensureTernakLoaded, 'konfirmasiBukaGeblak');
  window.cariHariPetungKehidupanUI = makeLazyProxy(ensureTernakLoaded, 'cariHariPetungKehidupanUI');
  window.pilihTanggalHasilCariTernak = makeLazyProxy(ensureTernakLoaded, 'pilihTanggalHasilCariTernak');
  window.switchTernakInputMode = makeLazyProxy(ensureTernakLoaded, 'switchTernakInputMode');
  window.cariTanggalSesuai1Bulan = makeLazyProxy(ensureTernakLoaded, 'cariTanggalSesuai1Bulan');

  // Sasmitha bindings
  window.initSasmithaUI = makeLazyProxy(ensureSasmithaLoaded, 'initSasmithaUI');
  window.switchSasmithaSubtab = makeLazyProxy(ensureSasmithaLoaded, 'switchSasmithaSubtab');
  window.onImpenSearchInput = makeLazyProxy(ensureSasmithaLoaded, 'onImpenSearchInput');
  window.onKedutSearchInput = makeLazyProxy(ensureSasmithaLoaded, 'onKedutSearchInput');
  window.onKedutCategorySelect = makeLazyProxy(ensureSasmithaLoaded, 'onKedutCategorySelect');
  window.onGerhanaSasiSelect = makeLazyProxy(ensureSasmithaLoaded, 'onGerhanaSasiSelect');
  window.onLinduSasiSelect = makeLazyProxy(ensureSasmithaLoaded, 'onLinduSasiSelect');
  window.onLinduWaktuToggle = makeLazyProxy(ensureSasmithaLoaded, 'onLinduWaktuToggle');
  window.pilihTitikAnatomiKedut = makeLazyProxy(ensureSasmithaLoaded, 'pilihTitikAnatomiKedut');
  window.setKedutViewMode = makeLazyProxy(ensureSasmithaLoaded, 'setKedutViewMode');
}

wireKalenderFeature();
wireJodohFeature();
wireSelametanFeature();
wireAksaraFeature();
wireWayangFeature();
wireAudioFeature();
wireNujumFeature();
wireSapaDinaFeature(); // Phase II: Sapa Dina

// ─── TAB SWITCHING & LAZY-LOAD ORCHESTRATOR ───────────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('tab-switched', async function (e) {
    const tabId = e.detail?.tabId;
    if (!tabId) return;

    switch (tabId) {
      case 'kepribadian':
        await ensureNujumLoaded();
        break;

      case 'wuku':
        await ensureWukuLoaded();
        if (typeof window.renderFullWukuPage === 'function') {
          window.renderFullWukuPage();
        }
        break;

      case 'perjodohan':
        if (typeof window.initPerjodohanSelects === 'function') {
          window.initPerjodohanSelects();
        }
        break;

      case 'selametan':
        if (typeof window.hitungSelametan === 'function') {
          window.hitungSelametan();
        }
        break;

      case 'ijab':
        {
          const m = await ensureIjabLoaded();
          if (m && typeof m.initIjabUI === 'function') m.initIjabUI();
        }
        break;

      case 'omah':
        {
          const m = await ensureOmahLoaded();
          if (m && typeof m.initOmahUI === 'function') m.initOmahUI();
        }
        break;

      case 'ternak':
        {
          const m = await ensureTernakLoaded();
          if (m && typeof m.initTernakUI === 'function') m.initTernakUI();
        }
        break;

      case 'sasmitha':
        {
          const m = await ensureSasmithaLoaded();
          if (m && typeof m.initSasmithaUI === 'function') m.initSasmithaUI();
        }
        break;

      case 'tanggal-jawa':
        if (typeof window.renderKonversiTanggalJawa === 'function') {
          window.renderKonversiTanggalJawa();
        }
        break;

      case 'gamelan':
        if (typeof window.renderGamelanKeys === 'function') {
          window.renderGamelanKeys();
        }
        break;

      case 'aksara':
        if (typeof window.initAksaraListeners === 'function') window.initAksaraListeners();
        if (typeof window.renderAksaraKeyboardPalette === 'function') window.renderAksaraKeyboardPalette();
        if (typeof window.initDrawingCanvas === 'function') window.initDrawingCanvas();
        if (typeof window.renderSandhanganGuidePanel === 'function') window.renderSandhanganGuidePanel();
        if (typeof window.restartAksaraQuiz === 'function') window.restartAksaraQuiz();
        break;

      case 'wayang':
        if (typeof window.renderWayangGrid === 'function') {
          const renderPromise = window.renderWayangGrid();
          if (renderPromise && typeof renderPromise.then === 'function') {
            await renderPromise;
          }
        }
        if (typeof window.initWayangDraggable === 'function') window.initWayangDraggable();
        if (typeof window.renderWayangEthicsBanner === 'function') window.renderWayangEthicsBanner();
        if (typeof window.selectWayangCharacter === 'function') window.selectWayangCharacter('arjuna');
        break;

      case 'pitutur':
        await ensurePituturLoaded();
        try {
          const kawruhMod = await import('./data/kawruh-boso.js');
          if (kawruhMod && typeof kawruhMod.renderKawruhBosoUI === 'function') {
            kawruhMod.renderKawruhBosoUI('kawruhBosoContainer');
          }
        } catch (e) {
          console.error('Gagal inisialisasi Kawruh Basa Jawa:', e);
        }
        break;

      case 'tumpeng':
        await ensureTumpengLoaded();
        break;

      case 'tripurusa':
        {
          const tripurusaModule = await import('./modules/budaya/tripurusa.js');
          if (tripurusaModule && typeof tripurusaModule.renderTripurusaModule === 'function') {
            tripurusaModule.renderTripurusaModule('tripurusaContentContainer');
          }
        }
        break;

      case 'ensiklopedia-budaya':
        {
          const ensikloModule = await import('./modules/budaya/ensiklopedia-budaya.js');
          if (ensikloModule && typeof ensikloModule.renderEnsiklopediaBudayaPage === 'function') {
            ensikloModule.renderEnsiklopediaBudayaPage('ensiklopediaBudayaContainer');
          }
        }
        break;

      case 'pustaka':
        {
          const m = await ensurePustakaLoaded();
          if (m && typeof m.initPustakaUI === 'function') {
            await m.initPustakaUI('pustakaContainer');
          }
        }
        break;

      case 'sinengker':
        {
          const m = await ensureSinengkerLoaded();
          if (m && typeof m.initSinengkerUI === 'function') {
            m.initSinengkerUI('sinengkerContainer');
          }
        }
        break;

      case 'mitologi':
        {
          const m = await ensureMitologiLoaded();
          if (m && typeof m.initMitologiUI === 'function') {
            await m.initMitologiUI('mitologiPillarsGrid');
          }
        }
        break;
    }
  });
}

// ─── SERVICE WORKER REGISTRATION ──────────────────────────────────────────
function registerServiceWorker() {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('./sw.js')
        .then((reg) => {
          console.log('[PWA] Service Worker terdaftar dengan scope:', reg.scope);
        })
        .catch((err) => {
          console.warn('[PWA] Gagal meregistrasi Service Worker:', err);
        });
    });
  }
}

// ─── DATABASE SERVICE (JSON DATABASE LOADER) ──────────────────────────────
import { loadDomainData } from './services/dbLoader.js';

// ─── BOOTSTRAP INITIAL APPLICATION STATE ──────────────────────────────────
export function bootstrap() {
  initI18n();
  initModeFeature();
  registerServiceWorker();

  // Migrasi bertahap dbLoader: trigger pemuatan domain inti secara asinkron
  loadDomainData('kalender').catch((e) => console.warn('[bootstrap] kalender domain fallback:', e));
  loadDomainData('sapa-dina').catch((e) => console.warn('[bootstrap] sapa-dina domain fallback:', e));

  // Pemuatan UI dengan boundary resilien agar halaman tidak pernah blank
  try { if (typeof window.initKalenderSelects === 'function') window.initKalenderSelects(); } catch (e) { console.warn(e); }
  try { if (typeof window.renderKalender === 'function') window.renderKalender(); } catch (e) { console.warn(e); }
  try { initQuickTodayBadge(); } catch (e) { console.warn(e); }

  // Phase II: Sapa Dina — Ringkasan Harian (inisialisasi setelah kalender)
  try { initSapaDina('sapa-dina-container'); } catch (e) { console.warn(e); }

  try { if (typeof window.initPerjodohanSelects === 'function') window.initPerjodohanSelects(); } catch (e) { console.warn(e); }
  try { if (typeof window.initIjabUI === 'function') window.initIjabUI(); } catch (e) { console.warn(e); }
  try { if (typeof window.initOmahUI === 'function') window.initOmahUI(); } catch (e) { console.warn(e); }
  try { if (typeof window.initTernakUI === 'function') window.initTernakUI(); } catch (e) { console.warn(e); }
  try { if (typeof window.initSasmithaUI === 'function') window.initSasmithaUI(); } catch (e) { console.warn(e); }
  try { if (typeof window.initMitologiUI === 'function') window.initMitologiUI(); } catch (e) { console.warn(e); }

  // Inisialisasi Fitur Tema & Kelir Wayang Panggung
  initThemeFeature();
  initKelirWayangFeature();

  // Inisialisasi default tanggal wafat hari ini bila belum terisi
  const tglWafatInput = document.getElementById('tglWafatInput');
  if (tglWafatInput && !tglWafatInput.value) {
    tglWafatInput.value = new Date().toISOString().slice(0, 10);
  }

  // Inisialisasi dropdown Tahun Hitung Nujum agar selalu terisi sejak awal
  const selTahun = document.getElementById('tahunHitungKepribadian');
  if (selTahun && selTahun.children.length === 0) {
    const curY = new Date().getFullYear();
    for (let y = 1940; y <= 2050; y++) {
      const opt = document.createElement('option');
      opt.value = y;
      opt.textContent = `${y} M`;
      if (y === curY) opt.selected = true;
      selTahun.appendChild(opt);
    }
  }

  // Inisialisasi default tanggal lahir Nujum dengan hari ini bila belum terisi
  const tglLahirKepribadianInput = document.getElementById('tglLahirKepribadian');
  if (tglLahirKepribadianInput && !tglLahirKepribadianInput.value) {
    tglLahirKepribadianInput.value = new Date().toISOString().slice(0, 10);
    if (typeof window.updateKepribadianQuickInfo === 'function') {
      window.updateKepribadianQuickInfo();
    }
  }

  // Inisialisasi UX Enhancements (jawa-v11: Hero Card, Realtime Form Calc, Sticky TOC, Wizard Stepper, Profile Weton)
  try { renderHeroTodayCard(); } catch (e) { console.warn(e); }
  try { initAllFormRealtimeCalculators(); } catch (e) { console.warn(e); }
  try { initStickyToc(); } catch (e) { console.warn(e); }
  try { initJodohWizard(); } catch (e) { console.warn(e); }
  try { loadUserProfileWeton(); } catch (e) { console.warn(e); }
  try { syncDocumentLangAttribute(getLanguage()); } catch (e) { console.warn(e); }

  initModalListeners();

  // Reaktif re-render seluruh modul aktif saat preferensi bahasa diubah
  if (typeof window !== 'undefined') {
    window.addEventListener('language-changed', () => {
      try { syncDocumentLangAttribute(getLanguage()); } catch (e) { /* ignore */ }
      try { renderHeroTodayCard(); } catch (e) { /* ignore */ }
      if (typeof window.renderKalender === 'function') {
        try { window.renderKalender(); } catch (e) { /* ignore */ }
      }
      if (typeof window.initSapaDina === 'function') {
        try { window.initSapaDina('sapa-dina-container'); } catch (e) { /* ignore */ }
      }
      if (typeof window.hitungNujumPribadi === 'function') {
        const container = document.getElementById('nujumResultContainer');
        if (container && container.innerHTML.trim()) {
          try { window.hitungNujumPribadi(); } catch (e) { /* ignore */ }
        }
      }
      if (typeof window.renderFullWukuPage === 'function') {
        const tabWuku = document.getElementById('tab-wuku');
        if (tabWuku && !tabWuku.classList.contains('hidden')) {
          try { window.renderFullWukuPage(); } catch (e) { /* ignore */ }
        }
      }
      if (typeof window.selectWukuDetail === 'function' && window.activeWukuNo) {
        try { window.selectWukuDetail(window.activeWukuNo); } catch (e) { /* ignore */ }
      }
      if (typeof window.renderWukuGrid === 'function' && typeof window.getAllWuku === 'function') {
        try { window.renderWukuGrid(window.getAllWuku()); } catch (e) { /* ignore */ }
      }
      if (typeof window.initMitologiUI === 'function') {
        try { window.initMitologiUI(); } catch (e) { /* ignore */ }
      }
      if (typeof window.hitungOmahDariUI === 'function') {
        const container = document.getElementById('omahResultContainer');
        if (container && container.innerHTML.trim()) {
          try { window.hitungOmahDariUI(false); } catch (e) { /* ignore */ }
        }
      }
      if (typeof window.hitungIjabDariUI === 'function') {
        const container = document.getElementById('ijabResultContainer');
        if (container && container.innerHTML.trim()) {
          try { window.hitungIjabDariUI(false); } catch (e) { /* ignore */ }
        }
        if (typeof window.setIjabWizardStep === 'function') {
          try {
            const curStep = document.getElementById('ijabStepBadge')?.textContent || 1;
            window.setIjabWizardStep(curStep, false);
          } catch (e) { /* ignore */ }
        }
      }
      if (typeof window.syncTernakDariTanggal === 'function') {
        const container = document.getElementById('ternakResultContainer');
        if (container && container.innerHTML.trim()) {
          try { window.syncTernakDariTanggal(); } catch (e) { /* ignore */ }
        }
      }
    });

    window.addEventListener('mode-changed', () => {
      try { initJodohWizard(); } catch (e) { /* ignore */ }
    });
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
}

// ─── BACKWARD COMPATIBLE EXPORTS ───────────────────────────────────────────
export {
  loadScript,
  ensureNujumLoaded,
  ensurePituturLoaded,
  ensureWukuLoaded,
  ensureTumpengLoaded,
  initQuickTodayBadge,
  closeAnyActiveModal,
  initModalListeners,
  registerServiceWorker,
  initI18n,
  toggleLanguage,
  setLanguage,
  getLanguage,
  getMode,
  setMode,
  isPemula,
  isAhli,
  hasModePreference,
  applyModeToUI,
  toggleMode
};
